import { useState, useEffect, useCallback } from "react";

interface RouteType {
    isAdmin: boolean;
    isSearch: boolean;
    searchQuery: string | undefined;
}

function detectRoute(): RouteType {
    const hash = window.location.hash;
    const isSearch = /\/search\//.test(hash);
    const isAdmin = /\/admin(\/|$|\?)/.test(hash);
    const params = new URLSearchParams(hash.split("?")[1] || "");
    const searchQuery = params.get("queryText") || undefined;

    return { isAdmin, isSearch, searchQuery };
}

// Module-level singleton for history method patching.
// Ensures the patch is applied exactly once regardless of how many hook instances exist.
const listeners = new Set<() => void>();
let patched = false;
let originalPushState: typeof history.pushState;
let originalReplaceState: typeof history.replaceState;

function patchHistoryMethods() {
    if (patched) return;
    patched = true;
    originalPushState = history.pushState.bind(history);
    originalReplaceState = history.replaceState.bind(history);
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
        originalPushState(...args);
        listeners.forEach((fn) => fn());
    };
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
        originalReplaceState(...args);
        listeners.forEach((fn) => fn());
    };
}

/**
 * Subscribe a handler to history pushState/replaceState changes.
 * Module-private — consumers should use useHistoryChangeSubscription() instead.
 */
function subscribe(handler: () => void): () => void {
    listeners.add(handler);
    patchHistoryMethods();
    return () => {
        listeners.delete(handler);
    };
}

export function useRouteType(): RouteType {
    const [routeType, setRouteType] = useState<RouteType>(() => detectRoute());

    const handler = useCallback(() => setRouteType(detectRoute()), []);
    useHistoryChangeSubscription(handler);

    return routeType;
}

/**
 * Hook that subscribes a handler to all route-change signals: monkey-patched
 * pushState/replaceState + hashchange + popstate. Encapsulates the triple-listen
 * pattern so sibling hooks (useRouteType, useHighlightParams) share the same
 * detection logic without directly importing the subscribe primitive.
 *
 * @param handler Called on any route change. Must be a stable reference
 *   (e.g. from useCallback) to avoid needless re-subscribes.
 */
export function useHistoryChangeSubscription(handler: () => void): void {
    useEffect(() => {
        const unsubscribe = subscribe(handler);
        window.addEventListener("hashchange", handler);
        window.addEventListener("popstate", handler);
        return () => {
            unsubscribe();
            window.removeEventListener("hashchange", handler);
            window.removeEventListener("popstate", handler);
        };
    }, [handler]);
}
